// Ch94 · REEL VERSION CHAINS — one tile per reel, versions inside.
//
// PKT-0792: "ONE tile per reel with versions inside" · a stage pill for
// "Replaced by a newer version" · and the binding rule "One card per video."
//
// 🔴 WHY THIS IS CLIENT-SIDE AND NOT A MIGRATION.
// Migration 104 (supersede-on-re-render) is MERGED BUT UNAPPLIED and flagged
// must-not-apply-as-written (PKT-0765: a republished parked card parks its own
// successor, and the rollback cannot run). We do not need it. The DTO already
// carries the chain — `parent_reel_id` ("the reel this one was revised FROM")
// and `revision_number` ("the real version counter") landed with migration 083,
// which IS applied. So "replaced by a newer version" is DERIVED here, from data
// already on the wire, and 104 stays untouched.
//
// 🔴 THE TRAP THIS MODULE EXISTS TO AVOID.
// The DTO's own comment says of the 083 fields: "ABSENT is the normal state
// until it is not." A card published before 083, or by a box that has not
// shipped the field yet, carries no chain at all. And the Reels tab paginates
// past 24 — so a reel's parent can be REAL and simply not on this page.
//
// In both cases the honest answer is "we cannot see this reel's history", which
// is NOT the same as "this reel has no newer version". A tile must never wear a
// "replaced" pill it inferred from absence, and must never assert "current"
// either. Same rule as the counts module: unknown and zero are different
// claims, and only one of them is safe to guess.
//
// No React, so every rule below is testable without a DOM.

/** The fields this module needs. A superset of the real DTO, structurally. */
export interface ChainableReel {
  id: string;
  reel_id: string;
  /** The reel this one was revised FROM. Absent on a first cut, and on old cards. */
  parent_reel_id?: string | null;
  /** The real version counter. `template_version` is a hardcoded "v1" and is not one. */
  revision_number?: number;
}

/** What we are able to say about one version's place in its chain. */
export type ChainPosition =
  /** A newer version of this reel exists, and we can see it. */
  | { kind: "replaced"; byReelId: string }
  /** This is the newest version we can see, and we can see the whole chain. */
  | { kind: "current" }
  /**
   * The chain leaves this page — a parent or child we cannot load. We say
   * nothing rather than guess, and `missingReelId` names what we could not see
   * so the UI can offer to go find it instead of inventing a state.
   */
  | { kind: "unknown"; missingReelId: string };

export interface ReelVersionGroup {
  /** The oldest ancestor we can see. Stable id for the tile. */
  rootReelId: string;
  /** Oldest first. One entry per version we can see. */
  versions: ChainableReel[];
  /** The version the tile shows by default: the newest we can see. */
  head: ChainableReel;
  /** Whether the tile may claim `head` is genuinely the latest. */
  headPosition: ChainPosition;
}

const reelIdOf = (r: ChainableReel): string => r.reel_id;

/**
 * Group a page of reels into one entry per chain.
 *
 * Order within a group is by `revision_number` when every member has one. If
 * ANY member is missing it, we fall back to the order the server sent — and we
 * do NOT claim a head position, because "newest" would then be a guess. An
 * invented ordering is how a superseded reel gets shown to Grant as current.
 */
export function groupReelVersions(reels: ChainableReel[]): ReelVersionGroup[] {
  const byReelId = new Map<string, ChainableReel>();
  reels.forEach((r) => byReelId.set(reelIdOf(r), r));

  // Walk each reel up to the oldest ancestor VISIBLE ON THIS PAGE. A parent we
  // cannot see ends the walk; the walk does not pretend it reached a root.
  const rootOf = new Map<string, { rootReelId: string; parentOffPage: string | null }>();
  reels.forEach((reel) => {
    const seen = new Set<string>();
    let current = reel;
    let parentOffPage: string | null = null;
    for (;;) {
      const currentId = reelIdOf(current);
      if (seen.has(currentId)) break; // defensive: a cycle is data corruption, not a chain
      seen.add(currentId);
      const parentId = current.parent_reel_id;
      if (!parentId) break;
      const parent = byReelId.get(parentId);
      if (!parent) {
        parentOffPage = parentId;
        break;
      }
      current = parent;
    }
    rootOf.set(reelIdOf(reel), { rootReelId: reelIdOf(current), parentOffPage });
  });

  const groups = new Map<string, ChainableReel[]>();
  reels.forEach((reel) => {
    const { rootReelId } = rootOf.get(reelIdOf(reel))!;
    const bucket = groups.get(rootReelId);
    if (bucket) bucket.push(reel);
    else groups.set(rootReelId, [reel]);
  });

  return Array.from(groups.entries()).map(([rootReelId, members]) => {
    const everyMemberNumbered = members.every(
      (m) => typeof m.revision_number === "number"
    );
    const versions = everyMemberNumbered
      ? [...members].sort((a, b) => a.revision_number! - b.revision_number!)
      : [...members];

    const head = versions[versions.length - 1];
    const offPageParent = members
      .map((m) => rootOf.get(reelIdOf(m))!.parentOffPage)
      .find((p): p is string => Boolean(p));

    let headPosition: ChainPosition;
    if (offPageParent) {
      // Part of this reel's history is not loaded. We cannot rule out a newer
      // version living wherever the rest of the chain is.
      headPosition = { kind: "unknown", missingReelId: offPageParent };
    } else if (!everyMemberNumbered && members.length > 1) {
      // We have siblings but no trustworthy order for them.
      headPosition = { kind: "unknown", missingReelId: reelIdOf(head) };
    } else {
      headPosition = { kind: "current" };
    }

    return { rootReelId, versions, head, headPosition };
  });
}

/**
 * Where one version sits in its own group.
 *
 * This is what drives the "Replaced by a newer version" stage pill. A version
 * is `replaced` ONLY when a newer sibling is visible — never by inference from
 * a missing field.
 */
export function positionOf(
  group: ReelVersionGroup,
  reel: ChainableReel
): ChainPosition {
  const index = group.versions.findIndex((v) => v.id === reel.id);
  if (index === -1) return { kind: "unknown", missingReelId: reelIdOf(reel) };

  const newer = group.versions[index + 1];
  if (newer) return { kind: "replaced", byReelId: reelIdOf(newer) };

  return group.headPosition;
}

/**
 * The stage pill's label, or `null` for "say nothing".
 *
 * `null` is a real and frequent answer. A tile whose history we cannot see gets
 * NO pill — not "current", not "replaced". Grant's words, no jargon.
 */
export function replacedPillLabel(position: ChainPosition): string | null {
  return position.kind === "replaced" ? "Replaced by a newer version" : null;
}

/** "3 versions" for the tile, or `null` when there is only the one. */
export function versionCountLabel(group: ReelVersionGroup): string | null {
  return group.versions.length > 1 ? `${group.versions.length} versions` : null;
}
